import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import {
  aprovarPedido,
  dataHora,
  dataHoraCurta,
  idade,
  listarPedidos,
  recusarPedido,
} from '../../acesso';
import type { Falha, PedidoDeAcesso, StatusDoPedido } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { ErroDaLista, mensagemDeFalha, useEscape } from './Equipe';
import { UNIDADE } from './unidade';
import './acesso.css';
import './aprovacoes.css';

/**
 * TELA 32 — CADASTROS E APROVAÇÕES. "Aprovar não é pintar uma etiqueta de verde."
 *
 * Fila à esquerda, revisão à direita. Tudo o que o administrador confirma (pessoa, função,
 * unidade, perfil e CÓDIGO DEFINITIVO) está visível antes do botão. O servidor aponta código em
 * uso e possível duplicidade; a tela mostra e PARA — nada se mescla sozinho, e com o código
 * duplicado o botão de aprovar fica bloqueado (e o servidor recusa mesmo assim).
 *
 * Aprovar concede EXATAMENTE o perfil de balconista. O pedido original nunca é apagado.
 */

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly pedidos: readonly PedidoDeAcesso[] };

const ABAS: readonly (readonly [StatusDoPedido, string])[] = [
  ['pendente', 'Pendentes'],
  ['aprovado', 'Aprovados'],
  ['nao_aprovado', 'Não aprovados'],
];

const MOTIVOS = ['Cadastro duplicado', 'Dados incompletos', 'Não faz parte da equipe'] as const;

export function Aprovacoes({
  sessao,
  pedidoInicial,
  aoMudarPendentes,
}: {
  sessao: Sessao;
  pedidoInicial: string | null;
  aoMudarPendentes: (n: number) => void;
}) {
  const [aba, setAba] = useState<StatusDoPedido>('pendente');
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [selecionado, setSelecionado] = useState<string | null>(pedidoInicial);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(
    async (qual: StatusDoPedido) => {
      setEstado({ fase: 'carregando' });
      const r = await listarPedidos(qual);
      if (!r.ok) return setEstado({ fase: 'erro', falha: r.falha });
      setEstado({ fase: 'pronto', pedidos: r.dados.pedidos });
      if (qual === 'pendente') aoMudarPendentes(r.dados.pedidos.length);
    },
    [aoMudarPendentes],
  );

  useEffect(() => {
    void carregar(aba);
  }, [aba, carregar]);

  const pedidos = estado.fase === 'pronto' ? estado.pedidos : [];
  // No desktop a primeira da fila já abre (fila à esquerda, revisão à direita). No celular a fila
  // é a tela: só abre a revisão quem tocou. `''` = a pessoa voltou para a fila de propósito.
  const estreito = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const atual =
    selecionado === ''
      ? null
      : (pedidos.find((p) => p.id === selecionado) ??
        (aba === 'pendente' && !estreito ? pedidos[0] : undefined) ??
        null);

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso') {
    return <SemAcesso area="Aprovações" />;
  }

  const decidido = (texto: string) => {
    setAviso(texto);
    setSelecionado(null);
    void carregar('pendente');
  };

  return (
    <div className="ac ap">
      <div className="ap-abas" role="tablist">
        {ABAS.map(([id, rotulo]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={aba === id}
            className={`ap-aba${aba === id ? ' ap-aba-ativa' : ''}`}
            onClick={() => {
              setAba(id);
              setSelecionado(null);
              setAviso(null);
            }}
          >
            {rotulo}
          </button>
        ))}
        <span className="ac-espaco" />
        <span className="ac-nota ap-abas-nota">
          tipos de solicitação vêm do fluxo real do sistema
        </span>
      </div>

      {aviso && (
        <div className="ap-aviso-ok" role="status">
          <Icone nome="check" tamanho={15} traco={2.2} />
          <span>{aviso}</span>
          <button type="button" className="ac-link" onClick={() => setAviso(null)}>
            Fechar
          </button>
        </div>
      )}

      {estado.fase === 'erro' && (
        <ErroDaLista
          titulo="Não foi possível carregar as solicitações"
          aoTentar={() => void carregar(aba)}
        />
      )}

      {aba === 'pendente' && estado.fase !== 'erro' && (
        <div className="ap-corpo">
          <div className={`ap-fila${atual ? ' ap-fila-com-detalhe' : ''}`}>
            <div className="ap-fila-cabeca">
              <div className="ap-fila-titulo">
                {estado.fase === 'pronto'
                  ? `${String(pedidos.length)} aguardando decisão`
                  : 'Carregando…'}
              </div>
              {pedidos[0] && (
                <div className="ap-fila-sub">mais antiga {textoDaIdade(pedidos[0].criadoEm)}</div>
              )}
            </div>
            <div className="ap-fila-lista">
              {estado.fase === 'carregando' && (
                <div className="ap-fila-esqueleto" aria-busy="true" />
              )}
              {pedidos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`ap-item${atual?.id === p.id ? ' ap-item-ativo' : ''}`}
                  onClick={() => setSelecionado(p.id)}
                >
                  <div className="ap-item-topo">
                    <span className="ap-item-tipo">NOVO CADASTRO</span>
                    <span className="ac-espaco" />
                    <span className="ap-item-idade">{idade(p.criadoEm)}</span>
                  </div>
                  <div className="ap-item-nome">{p.nome}</div>
                  <div className="ap-item-sub">Função solicitada: Balconista · {UNIDADE}</div>
                  {p.conflitos.length > 0 && (
                    <div className="ap-item-conflito">
                      <Icone nome="warn" tamanho={12} traco={1.8} />
                      {p.conflitos.length === 1
                        ? '1 conflito'
                        : `${String(p.conflitos.length)} conflitos`}
                    </div>
                  )}
                </button>
              ))}
              {estado.fase === 'pronto' && pedidos.length === 0 && (
                <div className="ap-tudo-em-dia">
                  <div className="ap-tudo-em-dia-selo">
                    <Icone nome="check" tamanho={20} traco={2.4} />
                  </div>
                  <div className="ap-tudo-em-dia-titulo">Nenhum cadastro aguardando aprovação</div>
                  <div className="ap-tudo-em-dia-texto">Tudo em dia.</div>
                </div>
              )}
              <div className="ap-fila-nota">
                Hoje a fila reúne os pedidos de acesso. Quando outros fluxos pedirem aprovação, cada
                domínio continua sendo a autoridade sobre o que é aprovado.
              </div>
            </div>
          </div>

          {atual && (
            <Revisao
              key={atual.id}
              pedido={atual}
              sessao={sessao}
              aoVoltar={() => setSelecionado('')}
              aoDecidir={decidido}
            />
          )}
          {!atual && estado.fase === 'pronto' && pedidos.length > 0 && (
            <div className="ap-detalhe ap-detalhe-vazio">Escolha uma solicitação na fila.</div>
          )}
        </div>
      )}

      {aba !== 'pendente' && estado.fase !== 'erro' && (
        <HistoricoDeDecisoes
          status={aba}
          pedidos={pedidos}
          carregando={estado.fase === 'carregando'}
        />
      )}
    </div>
  );
}

function textoDaIdade(iso: string): string {
  const t = idade(iso);
  return t.startsWith('hoje') ? `de ${t}` : t;
}

/* ================================================================== */

function Revisao({
  pedido: p,
  sessao,
  aoVoltar,
  aoDecidir,
}: {
  pedido: PedidoDeAcesso;
  sessao: Sessao;
  aoVoltar: () => void;
  aoDecidir: (texto: string) => void;
}) {
  const emUso = p.conflitos.find((c) => c.tipo === 'codigo_em_uso');
  const [codigo, setCodigo] = useState(emUso ? '' : (p.codigoDesejado ?? ''));
  const [confirmar, setConfirmar] = useState(false);
  const [recusar, setRecusar] = useState(false);
  const campoCodigo = useRef<HTMLInputElement>(null);

  const codigoLimpo = codigo.trim();
  const codigoInvalido = codigoLimpo !== '' && !/^[0-9A-Za-z._-]{1,40}$/.test(codigoLimpo);
  const repeteOEmUso =
    emUso !== undefined &&
    p.codigoDesejado !== null &&
    codigoLimpo.toLowerCase() === p.codigoDesejado.toLowerCase();
  const pendencia = !codigoLimpo
    ? 'Defina o código definitivo antes de aprovar'
    : codigoInvalido
      ? 'Código só com letras, números, ponto, hífen ou sublinhado'
      : repeteOEmUso
        ? 'Resolva as pendências antes de aprovar'
        : null;

  return (
    <div className="ap-detalhe">
      <button type="button" className="ap-voltar" onClick={aoVoltar}>
        <Icone nome="arrow-l" tamanho={16} traco={1.7} />
        Aprovações
      </button>
      <div className="ap-detalhe-cabeca">
        <div>
          <div className="ap-detalhe-nome-linha">
            <div className="ap-detalhe-nome">{p.nome}</div>
            <span className="ap-selo">AGUARDANDO APROVAÇÃO</span>
          </div>
          <div className="ap-detalhe-sub">
            Cadastro recebido em {dataHora(p.criadoEm)} · origem: tela de entrada (Solicitar acesso)
          </div>
        </div>
        <div className="ap-botoes">
          <button type="button" className="ac-botao ac-botao-g" onClick={() => setRecusar(true)}>
            Não aprovar
          </button>
          <div className="ap-aprovar">
            <button
              type="button"
              className="ac-botao ac-botao-g ac-botao-primario"
              disabled={pendencia !== null}
              onClick={() => setConfirmar(true)}
            >
              Aprovar cadastro
            </button>
            {pendencia && <span className="ap-pendencia">{pendencia}</span>}
          </div>
        </div>
      </div>

      <div className="ap-grade">
        <div className="ac-bloco">
          <div className="ac-rotulo-mono">DADOS DA SOLICITAÇÃO</div>
          <div className="ac-linha-dado">
            <span>Nome</span>
            <span>{p.nome}</span>
          </div>
          <div className="ac-linha-dado">
            <span>WhatsApp</span>
            <span className="ac-num">{p.whatsappMascarado ?? 'não informado'}</span>
          </div>
          <div className="ac-linha-dado">
            <span>Código solicitado</span>
            <span className="ac-mono">{p.codigoDesejado ?? 'nenhum'}</span>
          </div>
          <div className="ac-linha-dado">
            <span>Senha</span>
            <span className="ap-fraco">criada pela pessoa · ninguém vê</span>
          </div>
          <div className="ac-pe-bloco">
            Só o necessário para identificar e liberar acesso. Nada de documento, salário ou dado
            trabalhista.
          </div>
        </div>
        <div className="ac-bloco">
          <div className="ac-rotulo-mono ac-rotulo-mono-marca">O QUE SERÁ CONCEDIDO</div>
          <div className="ac-linha-dado">
            <span>Função</span>
            <span>Balconista</span>
          </div>
          <div className="ac-linha-dado">
            <span>Perfil de acesso</span>
            <span>Balconista</span>
          </div>
          <div className="ac-linha-dado">
            <span>Unidade</span>
            <span>{UNIDADE}</span>
          </div>
          <label className="ac-linha-dado">
            <span>Código definitivo</span>
            <input
              ref={campoCodigo}
              className={`ac-entrada ap-codigo${pendencia && codigoLimpo ? ' ap-codigo-erro' : ''}`}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="a definir"
              maxLength={40}
              spellCheck={false}
              autoCapitalize="none"
            />
          </label>
          <div className="ac-pe-bloco">
            Aprovar concede <b>exatamente</b> o perfil de balconista — nenhum acesso administrativo.
          </div>
        </div>
      </div>

      {p.conflitos.length > 0 && (
        <div className="ap-conflitos">
          <div className="ap-conflitos-titulo">
            <Icone nome="warn" tamanho={17} traco={1.7} />O servidor encontrou conflitos
          </div>
          <div className="ap-conflitos-lista">
            {p.conflitos.map((c) => {
              const [titulo, ...resto] = c.descricao.split(' · ');
              return (
                <div key={c.tipo} className="ap-conflito">
                  <div style={{ flex: 1 }}>
                    <div className="ap-conflito-titulo">{titulo}</div>
                    <div className="ap-conflito-texto">{resto.join(' · ')}</div>
                  </div>
                  {c.tipo === 'codigo_em_uso' ? (
                    <button
                      type="button"
                      className="ac-link"
                      onClick={() => {
                        setCodigo('');
                        campoCodigo.current?.focus();
                      }}
                    >
                      Escolher outro código
                    </button>
                  ) : (
                    <a className="ac-link" href="#/equipe">
                      Comparar
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          <div className="ap-conflitos-nota">
            Nada é mesclado automaticamente e a aprovação fica bloqueada pelo servidor enquanto o
            código estiver duplicado.
          </div>
        </div>
      )}

      <div className="ac-bloco">
        <div className="ac-rotulo-mono">HISTÓRICO DESTA SOLICITAÇÃO</div>
        <div className="ac-historico">
          <span className="ac-historico-hora">{dataHoraCurta(p.criadoEm)}</span>
          <div style={{ flex: 1 }}>Cadastro recebido</div>
        </div>
        <div className="ac-pe-bloco">
          Depois da decisão, o pedido original continua aqui — aprovar não apaga o que foi
          solicitado.
        </div>
      </div>

      {confirmar && (
        <ConfirmarAprovacao
          pedido={p}
          codigo={codigoLimpo}
          aoVoltar={() => setConfirmar(false)}
          aoAprovar={async () => {
            const r = await aprovarPedido(sessao, p.id, codigoLimpo);
            if (r.ok) {
              aoDecidir(`${p.nome} aprovado · Balconista · cód ${codigoLimpo}. Já pode entrar.`);
              return null;
            }
            return r.falha;
          }}
        />
      )}
      {recusar && (
        <NaoAprovar
          aoVoltar={() => setRecusar(false)}
          aoConfirmar={async (motivo) => {
            const r = await recusarPedido(sessao, p.id, motivo);
            if (r.ok) {
              aoDecidir(`Solicitação de ${p.nome} não aprovada · motivo: ${motivo}.`);
              return null;
            }
            return r.falha;
          }}
        />
      )}
    </div>
  );
}

function ConfirmarAprovacao({
  pedido: p,
  codigo,
  aoVoltar,
  aoAprovar,
}: {
  pedido: PedidoDeAcesso;
  codigo: string;
  aoVoltar: () => void;
  aoAprovar: () => Promise<Falha | null>;
}) {
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);
  useEscape(aoVoltar, !enviando);

  async function aprovar() {
    if (enviando) return;
    setEnviando(true);
    setFalha(null);
    const f = await aoAprovar();
    setEnviando(false);
    if (f) setFalha(f);
  }

  return (
    <div
      className="ac-veu ac-veu-centro"
      onClick={() => !enviando && aoVoltar()}
      role="presentation"
    >
      <div
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Confirmar aprovação"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(460px, 100%)' }}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">Confirmar aprovação</div>
          <div className="ac-modal-sub">Revise antes de conceder o acesso</div>
        </div>
        <div className="ac-modal-corpo">
          <div className="ac-efeitos">
            <div className="ac-linha-dado">
              <span>Pessoa</span>
              <span>{p.nome}</span>
            </div>
            <div className="ac-linha-dado">
              <span>Função</span>
              <span>Balconista</span>
            </div>
            <div className="ac-linha-dado">
              <span>Perfil de acesso</span>
              <span>Balconista</span>
            </div>
            <div className="ac-linha-dado">
              <span>Unidade</span>
              <span>{UNIDADE}</span>
            </div>
            <div className="ac-linha-dado">
              <span>Código</span>
              <span className="ac-mono" style={{ fontSize: 12.5 }}>
                {codigo}
              </span>
            </div>
          </div>
          <div className="ac-info" style={{ marginBottom: 11 }}>
            <span className="ac-caixinha" aria-hidden="true" />
            <div>
              Enviar acesso por WhatsApp{' '}
              <span className="ac-info-fraco">— quando o envio estiver disponível</span>
            </div>
          </div>
          {falha && (
            <div className="ac-erro" role="alert" style={{ marginBottom: 12 }}>
              <div className="ac-erro-titulo">Não foi possível aprovar o cadastro</div>
              <div className="ac-erro-texto">
                {mensagemDeFalha(falha)} A solicitação continua pendente. Nada foi concedido.
              </div>
            </div>
          )}
          <div className="ac-botoes-modal">
            <button
              type="button"
              className="ac-botao-cheio"
              disabled={enviando}
              onClick={() => void aprovar()}
            >
              {enviando ? 'Aprovando…' : falha ? 'Tentar de novo' : 'Aprovar e habilitar acesso'}
            </button>
            <button
              type="button"
              className="ac-botao-contorno"
              disabled={enviando}
              onClick={aoVoltar}
            >
              Voltar
            </button>
          </div>
          <div className="ac-pe-modal">
            A decisão registra você como responsável, com data e hora.
          </div>
        </div>
      </div>
    </div>
  );
}

function NaoAprovar({
  aoVoltar,
  aoConfirmar,
}: {
  aoVoltar: () => void;
  aoConfirmar: (motivo: string) => Promise<Falha | null>;
}) {
  const [motivo, setMotivo] = useState<string>(MOTIVOS[0]);
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);
  useEscape(aoVoltar, !enviando);

  async function confirmar() {
    if (enviando) return;
    setEnviando(true);
    setFalha(null);
    const texto = observacao.trim() ? `${motivo} · ${observacao.trim()}` : motivo;
    const f = await aoConfirmar(texto);
    setEnviando(false);
    if (f) setFalha(f);
  }

  return (
    <div
      className="ac-veu ac-veu-centro"
      onClick={() => !enviando && aoVoltar()}
      role="presentation"
    >
      <div
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Não aprovar cadastro"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(460px, 100%)' }}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo" style={{ fontSize: 15 }}>
            Não aprovar cadastro
          </div>
          <div className="ac-modal-sub">
            Linguagem neutra · a solicitação é encerrada, não excluída
          </div>
        </div>
        <div className="ac-modal-corpo">
          <div className="ac-efeitos-titulo">MOTIVO</div>
          <div className="ap-motivos">
            {MOTIVOS.map((m) => (
              <button
                key={m}
                type="button"
                className={`ap-motivo${motivo === m ? ' ap-motivo-ativo' : ''}`}
                aria-pressed={motivo === m}
                onClick={() => setMotivo(m)}
              >
                {m}
              </button>
            ))}
          </div>
          <textarea
            className="ac-texto-area"
            style={{ minHeight: 48 }}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Observação opcional"
            maxLength={400}
          />
          {falha && (
            <div className="ac-erro" role="alert" style={{ marginBottom: 12 }}>
              <div className="ac-erro-titulo">Não foi possível registrar a decisão</div>
              <div className="ac-erro-texto">
                {mensagemDeFalha(falha)} A solicitação continua pendente.
              </div>
            </div>
          )}
          <button
            type="button"
            className="ac-botao-contorno ac-botao-contorno-escuro"
            disabled={enviando}
            onClick={() => void confirmar()}
          >
            {enviando ? 'Registrando…' : 'Confirmar decisão'}
          </button>
          <button
            type="button"
            className="ac-link"
            style={{ display: 'block', margin: '12px auto 0' }}
            onClick={aoVoltar}
          >
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoricoDeDecisoes({
  status,
  pedidos,
  carregando,
}: {
  status: StatusDoPedido;
  pedidos: readonly PedidoDeAcesso[];
  carregando: boolean;
}) {
  const ordenados = useMemo(
    () => [...pedidos].sort((a, b) => (b.decididoEm ?? '').localeCompare(a.decididoEm ?? '')),
    [pedidos],
  );
  const aprovado = status === 'aprovado';
  return (
    <div className="ac-bloco ap-historico">
      <div className="ac-rotulo-mono">
        {aprovado ? 'CADASTROS APROVADOS' : 'SOLICITAÇÕES NÃO APROVADAS'}
      </div>
      {carregando && <div className="ap-fila-esqueleto" aria-busy="true" />}
      {!carregando && ordenados.length === 0 && (
        <div className="ac-vazio">
          <div className="ac-vazio-titulo">
            {aprovado ? 'Nenhum cadastro aprovado ainda' : 'Nenhuma solicitação recusada'}
          </div>
          <div className="ac-vazio-texto">As decisões aparecem aqui, com autor, hora e motivo.</div>
        </div>
      )}
      {ordenados.length > 0 && (
        <div className="ap-decisoes">
          {ordenados.map((p) => (
            <div key={p.id} className="ap-decisao">
              {aprovado ? (
                <span className="ap-decisao-sim">
                  <Icone nome="check" tamanho={16} traco={2.1} />
                </span>
              ) : (
                <span className="ap-decisao-nao">
                  <Icone nome="x" tamanho={16} traco={1.9} />
                </span>
              )}
              <div style={{ flex: 1 }}>
                <div className="ap-decisao-titulo">
                  {p.nome} · {aprovado ? 'aprovado' : 'não aprovado'}
                </div>
                <div className="ap-decisao-texto">
                  Por {p.decididoPor ?? '—'}
                  {p.decididoEm ? ` em ${dataHora(p.decididoEm)}` : ''}
                  {aprovado
                    ? ' · Balconista'
                    : p.motivoDecisao
                      ? ` · motivo: ${p.motivoDecisao}`
                      : ''}
                  {' · pedido de '}
                  {dataHoraCurta(p.criadoEm)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
